import { BitCrusher } from './effects/BitCrusher.js';
import { FoldDistortion } from './effects/FoldDistortion.js';
import { Fuzz } from './effects/Fuzz.js';
import { Reverb } from './effects/Reverb.js';
import { Tremolo } from './effects/Tremolo.js';
import { LFO } from './LFO.js';
import { Voice } from './Voice.js';


export class AudioEngine {
    constructor() {
        this.ctx = null;
        
        // Voice Management
        this.voices = [];
        this.voicePoolSize = 8;
        this.activeVoices = new Map(); // id -> Voice
        this.polyphonyMode = 'mono'; // 'mono' or 'poly'
        this.currentWaveform = 'sawtooth'; // Track globally to update new voices or all voices

        // Routing
        this.inputMixer = null; // Summing bus for all voices
        this.filterNode = null; 
        
        // Output
        this.masterGain = null;
        this.analyser = null;
        
        // Effects Storage
        this.effects = new Map(); 
        this.effectOrder = [];    
        
        this.customWaves = {};

        // MIDI State
        this.sustainActive = false;
        this.sustainedNotes = new Set();

        // Backend Configuration
        this.backend = 'web'; // 'web' or 'rust'
        this.voiceIdMap = new Map(); // Map arbitrary IDs to 0-7 for Rust backend
        this.nextVoiceIdx = 0;
    }

    async init() {
        if (this.ctx) await this.ctx.close();
        
        // Check for Tauri and Rust capability (Optional auto-switch could go here)
        // For now, we default to Web Audio API as it supports the full FX chain.
        
        const AudioContext = window.AudioContext || window.webkitAudioContext;
        this.ctx = new AudioContext({ latencyHint: 'interactive' });

        // 1. Core Nodes
        this.inputMixer = this.ctx.createGain(); // Bus
        this.masterGain = this.ctx.createGain();
        this.analyser = this.ctx.createAnalyser();

        // 2. Create Voice Pool
        this.voices = [];
        for (let i = 0; i < this.voicePoolSize; i++) {
            this.voices.push(new Voice(this.ctx, this.inputMixer));
        }

        // 3. Setup Effects
        await this.setupEffects();

        // 4. Configuration
        this.createCustomWaveforms();
        this.analyser.fftSize = 4096;
        this.masterGain.gain.value = 0.5;

        // 5. Initial Routing
        // Chain: Voices -> InputMixer -> [FX Chain] -> Master -> Analyser
        this.updateRouting();

        this.analyser.connect(this.ctx.destination);

        // 6. Setup LFOs (Multi-LFO System)
        this.lfos = [
            new LFO(this.ctx),
            new LFO(this.ctx)
        ];
        this.lfoTargets = ['none', 'none']; // Track target strings for UI recall

        // Default LFO 1
        this.lfos[0].setRate(1.0);
        this.lfos[0].setDepth(100);
        // Default LFO 2
        this.lfos[1].setRate(0.5);
        this.lfos[1].setDepth(50);

        return this.analyser;
    }

    async setupEffects() {
        // --- Filter (Native Node wrapped as Effect) ---
        const filter = this.ctx.createBiquadFilter();
        filter.type = 'lowpass';
        filter.frequency.value = 1000;
        filter.Q.value = 1;
        this.effects.set('filter', {
            node: filter,
            enabled: true,
            getInput: () => filter,
            getOutput: () => filter,
            setParam: (key, val) => {
                if (key === 'frequency') filter.frequency.setTargetAtTime(val, this.ctx.currentTime, 0.01);
                if (key === 'Q') filter.Q.value = val;
                if (key === 'detune') filter.detune.value = val;
                if (key === 'gain') filter.gain.value = val;
                if (key === 'type') {
                    const types = ['lowpass', 'highpass', 'bandpass', 'peaking'];
                    filter.type = types[Math.round(val)] || 'lowpass';
                }
            }
        });

        // --- Bit Crusher ---
        const crusher = new BitCrusher(this.ctx);
        await crusher.setup();
        this.effects.set('crusher', {
            instance: crusher,
            enabled: true,
            getInput: () => crusher.getInput(),
            getOutput: () => crusher.getOutput(),
            setParam: (key, val) => {
                if (key === 'bits') crusher.setBits(val);
                if (key === 'rate') crusher.setNormFreq(val);
                if (key === 'mix') crusher.setMix(val);
            }
        });

        // --- Fold Distortion ---
        const folder = new FoldDistortion(this.ctx);
        await folder.setup();
        this.effects.set('distortion', {
            instance: folder,
            enabled: true,
            getInput: () => folder.getInput(),
            getOutput: () => folder.getOutput(),
            setParam: (key, val) => {
                if (key === 'drive') folder.setDrive(val);
                if (key === 'mix') folder.setMix(val);
            }
        });

        // --- Fuzz ---
        const fuzz = new Fuzz(this.ctx);
        await fuzz.setup();
        this.effects.set('fuzz', {
            instance: fuzz,
            enabled: true,
            getInput: () => fuzz.getInput(),
            getOutput: () => fuzz.getOutput(),
            setParam: (key, val) => {
                if (key === 'fuzz') fuzz.setFuzz(val);
                if (key === 'tone') fuzz.setTone(val);
                if (key === 'mix') fuzz.setMix(val);
            }
        });

        // --- Delay ---
        const delay = this.ctx.createDelay(2.0);
        const feedback = this.ctx.createGain();
        const delayWet = this.ctx.createGain();
        const delayInput = this.ctx.createGain();
        const delayOutput = this.ctx.createGain();
        
        // Internal Delay Routing
        delayInput.connect(delayOutput); // Dry pass-through
        delayInput.connect(delay);
        delay.connect(feedback);
        feedback.connect(delay);
        delay.connect(delayWet);
        delayWet.connect(delayOutput);

        delayWet.gain.value = 0.5;
        feedback.gain.value = 0.4;
        delay.delayTime.value = 0.3;

        this.effects.set('delay', {
            nodes: { delay, feedback, delayWet },
            enabled: true,
            getInput: () => delayInput,
            getOutput: () => delayOutput,
            setParam: (key, val) => {
                if (key === 'time') delay.delayTime.linearRampToValueAtTime(val, this.ctx.currentTime + 0.1);
                if (key === 'feedback') feedback.gain.value = val;
                if (key === 'mix') delayWet.gain.value = val;
            }
        });

        // --- Tremolo ---
        const tremolo = new Tremolo(this.ctx);
        await tremolo.setup();
        this.effects.set('tremolo', {
            instance: tremolo,
            enabled: true,
            getInput: () => tremolo.getInput(),
            getOutput: () => tremolo.getOutput(),
            setParam: (key, val) => {
                if (key === 'rate') tremolo.setRate(val);
                if (key === 'depth') tremolo.setDepth(val);
                if (key === 'mix') tremolo.setMix(val);
            }
        });

        // --- Reverb ---
        const reverb = new Reverb(this.ctx);
        await reverb.setup();
        this.effects.set('reverb', {
            instance: reverb,
            enabled: true,
            getInput: () => reverb.getInput(),
            getOutput: () => reverb.getOutput(),
            setParam: (key, val) => {
                if (key === 'decay') reverb.setDecay(val);
                if (key === 'mix') reverb.setMix(val);
            }
        });

        // Default Order
        this.effectOrder = ['filter', 'distortion', 'fuzz', 'crusher', 'tremolo', 'delay', 'reverb'];
    }

    toggleEffect(id, isEnabled) {
        const fx = this.effects.get(id);
        if (fx) {
            fx.enabled = isEnabled;
            this.updateRouting();
        }
    }

    updateRouting(newOrder) {
        if (newOrder) this.effectOrder = newOrder;

        // Disconnect everything first
        this.inputMixer.disconnect();
        this.effects.forEach(fx => fx.getOutput().disconnect());

        // Rebuild Chain
        let currentNode = this.inputMixer;
        const activeChain = [];

        this.effectOrder.forEach(id => {
            const fx = this.effects.get(id);
            if (fx && fx.enabled) {
                currentNode.connect(fx.getInput());
                currentNode = fx.getOutput();
                activeChain.push(id);
            }
        });

        console.log("Audio Chain:", activeChain.join(" -> ") || "Direct");

        // Connect last to Master
        currentNode.connect(this.masterGain);
        
        // Master always connected to Analyser from init
        this.masterGain.connect(this.analyser);
    }

    createCustomWaveforms() {
        // 1. Organ
        const real1 = new Float32Array([0, 1, 0.5, 0.25, 0.1, 0.5, 0.1, 0]); 
        const imag1 = new Float32Array(real1.length);
        this.customWaves['organ'] = this.ctx.createPeriodicWave(real1, imag1);

        // 2. Retro
        const size = 16;
        const real2 = new Float32Array(size);
        const imag2 = new Float32Array(size);
        for (let i = 1; i < size; i++) real2[i] = Math.random() * (1 - (i/size));
        this.customWaves['retro'] = this.ctx.createPeriodicWave(real2, imag2);

        // 3. Bass (Strong fundamental + odd harmonics)
        const real3 = new Float32Array([0, 1, 0, 0.6, 0, 0.3, 0, 0.1]);
        const imag3 = new Float32Array(real3.length);
        this.customWaves['bass'] = this.ctx.createPeriodicWave(real3, imag3);

        // 4. Vocal (Formant-ish: clusters of harmonics)
        const real4 = new Float32Array(32);
        const imag4 = new Float32Array(32);
        // Formant 1 (~500hz) and Formant 2 (~1500hz)
        real4[1] = 1.0; real4[2] = 0.5; // Fundamental
        real4[5] = 0.8; real4[6] = 0.4; // Formant 1 area
        real4[12] = 0.6; real4[13] = 0.3; // Formant 2 area
        this.customWaves['vocal'] = this.ctx.createPeriodicWave(real4, imag4);

        // 5. Ethereal (Soft, sparse high harmonics)
        const real5 = new Float32Array(64);
        const imag5 = new Float32Array(64);
        real5[1] = 1.0; 
        real5[4] = 0.4;
        real5[8] = 0.2;
        real5[16] = 0.1;
        real5[32] = 0.05;
        this.customWaves['ethereal'] = this.ctx.createPeriodicWave(real5, imag5);

        // 6. Crunch (Dense, rich harmonics with boosted evens)
        const real6 = new Float32Array(32);
        const imag6 = new Float32Array(32);
        for (let i = 1; i < 32; i++) {
            real6[i] = 1 / i; 
            if (i % 2 === 0) real6[i] *= 1.5; 
        }
        this.customWaves['crunch'] = this.ctx.createPeriodicWave(real6, imag6);

        // 7. Metallic (Sparse, high-frequency harmonics)
        const real7 = new Float32Array(64);
        const imag7 = new Float32Array(64);
        real7[1] = 0.5;
        real7[7] = 0.8;
        real7[13] = 0.6;
        real7[19] = 0.4;
        real7[33] = 0.3;
        real7[51] = 0.2;
        this.customWaves['metallic'] = this.ctx.createPeriodicWave(real7, imag7);

        // 8. Dystopia (Dark, inharmonic, Sci-Fi texture)
        const real8 = new Float32Array(64);
        const imag8 = new Float32Array(64);
        real8[1] = 0.9; // Fundamental
        real8[2] = 0.3; // Octave
        // Dissonant harmonics
        real8[11] = 0.5; // Tritone-ish
        real8[13] = 0.4;
        real8[17] = 0.3;
        real8[19] = 0.3;
        // High frequency wash
        for (let i = 40; i < 64; i+=3) {
             real8[i] = 0.1;
        }
        this.customWaves['dystopia'] = this.ctx.createPeriodicWave(real8, imag8);
    }

    async setBackend(mode) {
        if (mode === 'rust' && window.__TAURI__) {
            try {
                const msg = await (window.__TAURI__.core || window.__TAURI__.tauri).invoke('init_audio');
                console.log(msg);
                this.backend = 'rust';
                console.log("Switched to Rust Audio Backend");
                return true;
            } catch (e) {
                console.error("Failed to init Rust Audio:", e);
                return false;
            }
        } else {
            this.backend = 'web';
            console.log("Switched to Web Audio Backend");
            return true;
        }
    }

    getVoiceIndex(id) {
        if (!this.voiceIdMap.has(id)) {
            this.voiceIdMap.set(id, this.nextVoiceIdx);
            this.nextVoiceIdx = (this.nextVoiceIdx + 1) % 8;
        }
        return this.voiceIdMap.get(id);
    }

    setWaveform(type) {
        this.currentWaveform = type;
        
        if (this.backend === 'rust') {
            if (window.__TAURI__) {
                (window.__TAURI__.core || window.__TAURI__.tauri).invoke('rust_set_waveform', { wave: type });
            }
            return;
        }

        this.voices.forEach(voice => {
            if (this.customWaves[type]) {
                voice.setWaveform('custom', this.customWaves[type]);
            } else {
                voice.setWaveform(type);
            }
        });
    }

    // --- Parameter Proxies ---
    setEffectParam(id, key, val) {
        if (this.backend === 'rust') return; // FX not supported in Rust backend yet
        const fx = this.effects.get(id);
        if (fx) fx.setParam(key, val);
    }

    setPolyphonyMode(mode) {
        this.polyphonyMode = mode;
        // Kill all voices on mode switch
        this.allNotesOff();
    }

    setPitchBend(normalizedVal) {
        if (this.backend === 'rust') return; // Not impl
        // Range: -1.0 to 1.0
        // Standard bend: +/- 2 semitones = +/- 200 cents
        const cents = normalizedVal * 200;
        
        // Update all voices (active or not, so next note has bend)
        this.voices.forEach(voice => {
            voice.osc.detune.setTargetAtTime(cents, this.ctx.currentTime, 0.01);
        });
    }

    setSustain(isActive) {
        this.sustainActive = isActive;
        if (!isActive) {
            // Release all sustained notes
            this.sustainedNotes.forEach(id => {
                this.triggerRelease(id);
            });
            this.sustainedNotes.clear();
        }
    }

    setPitch(freq, id = 'mono', time = null) {
        if (this.backend === 'rust') {
            if (window.__TAURI__) {
                const idx = this.getVoiceIndex(id);
                // Rust backend currently only has 'trigger_attack', need 'set_pitch' command?
                // Or just re-trigger? Re-triggering might click.
                // For now, ignore pitch slide in Rust or implement update command.
                // Let's implement update in next iteration if needed.
            }
            return;
        }

        // console.log(`[Engine] Set Pitch: ${freq} for ${id}`);
        if (this.polyphonyMode === 'mono') {
            // In mono, update all active (should be 1) or just the last used
            // For simplicity, update all, though in mono we likely track one ID.
            this.voices[0].setPitch(freq, time);
        } else {
            const voice = this.activeVoices.get(id);
            if (voice) voice.setPitch(freq, time);
        }
    }

    triggerAttack(freq, maxGain = 0.5, id = 'mono', time = null) {
        if (this.backend === 'rust') {
            if (window.__TAURI__) {
                const idx = this.getVoiceIndex(id);
                (window.__TAURI__.core || window.__TAURI__.tauri).invoke('rust_trigger_attack', { id: idx, freq, gain: maxGain });
            }
            return;
        }

        if (this.polyphonyMode === 'mono') {
            // Use Voice 0 always
            this.activeVoices.set('mono', this.voices[0]);
            this.voices[0].triggerAttack(freq, maxGain, 'mono', time);
        } else {
            // Poly: Find free voice
            // 1. Check if ID already has a voice (retrigger/slide)
            if (this.activeVoices.has(id)) {
                const v = this.activeVoices.get(id);
                v.triggerAttack(freq, maxGain, id, time);
                return;
            }

            // 2. Find inactive voice
            const freeVoice = this.voices.find(v => !v.active);
            if (freeVoice) {
                this.activeVoices.set(id, freeVoice);
                freeVoice.triggerAttack(freq, maxGain, id, time);
            } else {
                // Steal voice? For now, just ignore if full
                console.warn("Max polyphony reached");
            }
        }
    }

    triggerRelease(id = 'mono', time = null) {
        // If sustain is active, defer release
        if (this.sustainActive) {
            if (this.polyphonyMode === 'poly') {
                 // In poly mode, we track the note ID to release later
                 // Check if it's actually playing first
                 if (this.activeVoices.has(id)) {
                     this.sustainedNotes.add(id);
                 }
                 return;
            } else {
                // Mono mode sustain? Usually just holds the last note.
                // For now, let's just ignore release in mono too
                this.sustainedNotes.add('mono');
                return;
            }
        }

        if (this.backend === 'rust') {
            if (window.__TAURI__) {
                const idx = this.getVoiceIndex(id);
                (window.__TAURI__.core || window.__TAURI__.tauri).invoke('rust_trigger_release', { id: idx });
            }
            return;
        }

        if (this.polyphonyMode === 'mono') {
            this.voices[0].triggerRelease(time);
            this.activeVoices.delete('mono');
            this.sustainedNotes.delete('mono');
        } else {
            const voice = this.activeVoices.get(id);
            if (voice) {
                voice.triggerRelease(time);
                this.activeVoices.delete(id);
                this.sustainedNotes.delete(id);
            }
        }
    }

    allNotesOff() {
        if (this.backend === 'rust') {
            // Loop 0-7 release
            if (window.__TAURI__) {
                for(let i=0; i<8; i++) {
                    (window.__TAURI__.core || window.__TAURI__.tauri).invoke('rust_trigger_release', { id: i });
                }
            }
            return;
        }
        this.voices.forEach(v => v.triggerRelease());
        this.activeVoices.clear();
        this.sustainedNotes.clear();
    }

    setMasterVolume(vol) {
        if (this.masterGain) this.masterGain.gain.value = vol;
    }

    getMasterVolume() {
        return this.masterGain ? this.masterGain.gain.value : 0;
    }
    
    getAnalyser() {
        return this.analyser;
    }
    
    async resume() {
        if (this.ctx && this.ctx.state === 'suspended') {
            await this.ctx.resume();
        }
    }

    toggleMute() {
        if (!this.ctx) return false;
        if (this.ctx.state === 'running') {
            this.ctx.suspend();
            return true;
        } else {
            this.ctx.resume();
            return false;
        }
    }

    // --- LFO Control ---
    setLFOParam(index, param, value) {
        const lfo = this.lfos[index];
        if (!lfo) return;
        
        // console.log(`Set LFO ${index} ${param}: ${value}`);
        if (param === 'rate') lfo.setRate(value);
        if (param === 'depth') lfo.setDepth(value);
        if (param === 'waveform') lfo.setWaveform(value);
    }

    setLFOTarget(index, targetStr) {
        const lfo = this.lfos[index];
        if (!lfo) return;
        
        // Update state tracking
        this.lfoTargets[index] = targetStr;

        // Always disconnect previous targets first
        lfo.disconnect();
        
        console.log(`[LFO ${index}] Target set to: ${targetStr}`);
        if (targetStr === 'none') {
            return;
        }
        
        const params = this.getParam(targetStr);
        if (params) {
            // Support array of params (for polyphonic oscillators) or single param
            if (Array.isArray(params)) {
                console.log(`[LFO ${index}] Connecting to ${params.length} targets`);
                params.forEach(p => lfo.connect(p));
            } else {
                console.log(`[LFO ${index}] Connecting to single target`);
                lfo.connect(params);
            }
        } else {
            console.warn(`[LFO ${index}] Target param '${targetStr}' not found.`);
        }
    }

    getLFOState(index) {
        const lfo = this.lfos[index];
        if (!lfo) return null;
        return {
            rate: lfo.osc.frequency.value,
            depth: lfo.gain.gain.value,
            waveform: lfo.osc.type,
            target: this.lfoTargets[index] || 'none'
        };
    }

    getParam(targetStr) {
        // format: "module:param"
        const [module, param] = targetStr.split(':');

        if (module === 'osc') {
            if (param === 'pitch' || param === 'frequency') {
                // Return array of all oscillator detune params for modulation
                // Using detune avoids conflicts with main frequency pitch control
                return this.voices.map(v => v.osc.detune);
            }
        }

        if (module === 'filter') {
            const fx = this.effects.get('filter');
            if (!fx) return null;
            if (param === 'cutoff' || param === 'frequency') return fx.node.frequency;
            if (param === 'res' || param === 'Q') return fx.node.Q;
            if (param === 'detune') return fx.node.detune;
        }

        if (module === 'distortion') {
            const fx = this.effects.get('distortion');
            if (!fx || !fx.instance.node) return null;
            return fx.instance.node.parameters.get(param); // 'drive', 'mix'
        }

        if (module === 'fuzz') {
            const fx = this.effects.get('fuzz');
            if (!fx || !fx.instance) return null;
            if (param === 'fuzz') return fx.instance.preGain.gain;
            if (param === 'tone') return fx.instance.tone.frequency;
            // Mix is gain node gain
        }

        if (module === 'crusher') {
            const fx = this.effects.get('crusher');
            if (!fx || !fx.instance) return null;
            if (param === 'bits') return fx.instance.node.parameters.get('bits');
            if (param === 'rate') return fx.instance.node.parameters.get('normfreq');
            if (param === 'mix') return fx.instance.wetGain.gain;
        }

        if (module === 'delay') {
            const fx = this.effects.get('delay');
            if (!fx) return null;
            if (param === 'time') return fx.nodes.delay.delayTime;
            if (param === 'feedback') return fx.nodes.feedback.gain;
        }

        if (module === 'tremolo') {
            const fx = this.effects.get('tremolo');
            if (!fx || !fx.instance) return null;
            if (param === 'rate') return fx.instance.lfo.frequency;
            // Depth/Mix in Tremolo controls a GainNode.gain, but setDepth sets target.
            // However, LFO needs an AudioParam to connect to.
            // My Tremolo implementation uses `lfoGain` to control depth of modulation.
            // If we want to MODULATE the rate or depth, we need to return those params.
            if (param === 'depth') return fx.instance.lfoGain.gain; 
        }

        if (module === 'lfo1') {
             if (param === 'rate') return this.lfos[0].osc.frequency;
             if (param === 'depth') return this.lfos[0].gain.gain;
        }

        if (module === 'lfo2') {
            if (param === 'rate') return this.lfos[1].osc.frequency;
            if (param === 'depth') return this.lfos[1].gain.gain;
       }

        return null;
    }
}